import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Paper,
  Stack,
  Typography,
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import type { DatePreset } from "./ParticipantCoverageFilters";
import { apiService } from "../../../config/api";
import { COMMON_FITBIT_HEALTHKIT_METRIC_KEYS } from "../../../constants/dataCoverageCommonMetrics";
import {
  getMetricsForDeviceType,
  METRIC_LABELS,
} from "../../../constants/metricCategories";
import { getDeviceType, type DeviceTypeParticipant } from "../../../utils/deviceType";
import ReactECharts from "echarts-for-react";
import type { EChartsOption } from "echarts";

interface MetricCoverageHeatmapCardProps {
  participantId: string;
  selectedParticipant:
    | (DeviceTypeParticipant & { firstName?: string; lastName?: string })
    | null;
}

interface HeatmapData {
  metricLabels: string[];
  periodLabels: string[];
  coverageMatrix: number[][];
}

interface FitbitRefreshRange {
  startDate: string;
  endDate: string;
  dayCount: number;
  statusMessage: string;
}

interface MetricOption {
  key: string;
  label: string;
}

const COMMON_METRIC_KEY_SET: Set<string> = new Set(
  COMMON_FITBIT_HEALTHKIT_METRIC_KEYS as readonly string[],
);

const MIN_DATE_RANGE = 21; // days
const AGGREGATION_TYPES: ("weekly" | "monthly" | "yearly")[] = [
  "weekly",
  "monthly",
  "yearly",
];

const MetricCoverageHeatmapCard: React.FC<MetricCoverageHeatmapCardProps> = ({
  participantId,
  selectedParticipant,
}) => {
  const [fromDate, setFromDate] = React.useState("");
  const [endDate, setEndDate] = React.useState("");
  const [preset, setPreset] = React.useState<DatePreset | null>(null);
  const [aggregationType, setAggregationType] = React.useState<
    "weekly" | "monthly" | "yearly"
  >("monthly");
  const [availableMetrics, setAvailableMetrics] = React.useState<
    MetricOption[]
  >([]);
  const [selectedMetrics, setSelectedMetrics] = React.useState<Set<string>>(
    new Set(),
  );
  const [running, setRunning] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [heatmapData, setHeatmapData] = React.useState<HeatmapData | null>(
    null,
  );
  const [deviceType, setDeviceType] = React.useState<
    "fitbit" | "healthkit" | "both" | "none"
  >("none");
  const [validationError, setValidationError] = React.useState<string | null>(
    null,
  );
  const [metricsExpanded, setMetricsExpanded] = React.useState(false);
  const [fetchStatus, setFetchStatus] = React.useState<string | null>(null);
  const [failedFitbitRanges, setFailedFitbitRanges] = React.useState<
    FitbitRefreshRange[]
  >([]);

  // Initialize dates on mount and when participantId changes
  React.useEffect(() => {
    const today = new Date();
    const end = today.toISOString().slice(0, 10);
    const start = new Date(today.getTime() - 89 * 24 * 60 * 60 * 1000)
      .toISOString()
      .slice(0, 10);
    setFromDate(start);
    setEndDate(end);
    setPreset(null);
    setSelectedMetrics(new Set());
    setHeatmapData(null);
    setError(null);
    setFetchStatus(null);
    setFailedFitbitRanges([]);
    setMetricsExpanded(false);
  }, [participantId]);

  // Determine device type and available metrics
  React.useEffect(() => {
    if (!selectedParticipant) return;

    const inferredDeviceType = getDeviceType(selectedParticipant);
    setDeviceType(inferredDeviceType);

    const metrics = getMetricsForDeviceType(inferredDeviceType).map(
      (metricKey) => ({
        key: metricKey,
        label: METRIC_LABELS[metricKey] || metricKey,
      }),
    );

    setAvailableMetrics(metrics);
    setSelectedMetrics(new Set());
  }, [selectedParticipant]);

  const applyPreset = (nextPreset: DatePreset | null) => {
    setPreset(nextPreset);
    if (!nextPreset) return;

    const today = new Date();
    const end = today.toISOString().slice(0, 10);
    let start = new Date(today);

    if (nextPreset === "7d") {
      start = new Date(today.getTime() - 6 * 24 * 60 * 60 * 1000);
    } else if (nextPreset === "1m") {
      start = new Date(today);
      start.setMonth(start.getMonth() - 1);
      start.setDate(start.getDate() + 1);
    } else if (nextPreset === "6m") {
      start = new Date(today);
      start.setMonth(start.getMonth() - 6);
      start.setDate(start.getDate() + 1);
    } else {
      start = new Date(today);
      start.setFullYear(start.getFullYear() - 1);
      start.setDate(start.getDate() + 1);
    }

    setFromDate(start.toISOString().slice(0, 10));
    setEndDate(end);
  };

  const toggleMetric = (metricKey: string, checked: boolean) => {
    const newSelected = new Set(selectedMetrics);
    if (checked) {
      newSelected.add(metricKey);
    } else {
      newSelected.delete(metricKey);
    }
    setSelectedMetrics(newSelected);
  };

  const selectAll = () => {
    setSelectedMetrics(new Set(availableMetrics.map((m) => m.key)));
  };

  const selectCommonOnly = () => {
    setSelectedMetrics(
      new Set(
        availableMetrics
          .filter((metric) => COMMON_METRIC_KEY_SET.has(metric.key))
          .map((metric) => metric.key),
      ),
    );
  };

  const commonMetricCount = availableMetrics.filter((metric) =>
    COMMON_METRIC_KEY_SET.has(metric.key),
  ).length;

  const deselectAll = () => {
    setSelectedMetrics(new Set());
  };

  const validateInputs = (): boolean => {
    setValidationError(null);

    if (!fromDate || !endDate) {
      setValidationError("Please select both from and end dates.");
      return false;
    }

    if (!participantId) {
      setValidationError("Participant ID is missing.");
      return false;
    }

    if (selectedMetrics.size === 0) {
      setValidationError("Please select at least one metric.");
      return false;
    }

    const from = new Date(fromDate);
    const to = new Date(endDate);
    const daysDiff = Math.floor(
      (to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24),
    );

    if (daysDiff < MIN_DATE_RANGE) {
      setValidationError(`Date range must be at least ${MIN_DATE_RANGE} days.`);
      return false;
    }

    // Validate aggregation type based on date range
    if (aggregationType === "weekly" && daysDiff < 14) {
      setValidationError("Weekly aggregation requires at least 14 days.");
      return false;
    }

    if (aggregationType === "monthly" && daysDiff < 30) {
      setValidationError("Monthly aggregation requires at least 30 days.");
      return false;
    }

    if (aggregationType === "yearly" && daysDiff < 365) {
      setValidationError("Yearly aggregation requires at least 365 days.");
      return false;
    }

    return true;
  };

  const handleGenerateHeatmap = async () => {
    if (!validateInputs()) return;

    setRunning(true);
    setError(null);
    setHeatmapData(null);
    setFetchStatus(null);
    setFailedFitbitRanges([]);

    try {
      const response = await apiService.post(
        `/api/data-coverage/participant/${participantId}/metric-coverage-heatmap`,
        {
          fromDate,
          endDate,
          aggregationType,
          selectedMetrics: Array.from(selectedMetrics),
          deviceType,
          refreshIfMissing: true,
        },
      );

      if (!response || !response.success) {
        throw new Error(response?.error || "Failed to generate heatmap");
      }

      setHeatmapData(response.data.heatmap);
      setFetchStatus(response.data?.fetch?.statusMessage || null);
      setFailedFitbitRanges(
        response.data?.fetch?.failedFitbitRefreshRanges || [],
      );
    } catch (err: any) {
      const errMsg =
        err?.response?.data?.error ||
        err?.message ||
        "Failed to generate heatmap";
      setError(errMsg);
    } finally {
      setRunning(false);
    }
  };

  const renderHeatmap = () => {
    if (!heatmapData) return null;

    const { metricLabels, periodLabels, coverageMatrix } = heatmapData;

    if (coverageMatrix.length === 0 || coverageMatrix[0].length === 0) {
      return (
        <Alert severity="info">No data available for selected criteria.</Alert>
      );
    }

    const seriesData: Array<[number, number, number]> = [];
    coverageMatrix.forEach((row, rowIndex) => {
      row.forEach((value, columnIndex) => {
        seriesData.push([columnIndex, rowIndex, value]);
      });
    });

    const option: EChartsOption = {
      backgroundColor: "#ffffff",
      grid: {
        left: 150,
        right: 40,
        top: 60,
        bottom: 110,
        containLabel: true,
      },
      tooltip: {
        position: "top",
        formatter: (params: any) => {
          const [xIndex, yIndex, value] = params.data as [
            number,
            number,
            number,
          ];
          const metric = metricLabels[yIndex] || "";
          const period = periodLabels[xIndex] || "";
          return `${metric}<br/>${period}<br/><b>Coverage:</b> ${Number(value).toFixed(1)}%`;
        },
      },
      xAxis: {
        type: "category",
        data: periodLabels,
        splitArea: { show: true },
        axisLabel: {
          rotate: 45,
          interval: 2,
          fontSize: 10,
        },
        axisTick: { alignWithLabel: true },
      },
      yAxis: {
        type: "category",
        data: metricLabels,
        splitArea: { show: true },
        axisLabel: {
          fontSize: 11,
          width: 190,
          overflow: "truncate",
        },
      },
      visualMap: {
        min: 0,
        max: 100,
        calculable: true,
        orient: "horizontal",
        left: "center",
        bottom: 20,
        text: ["100%", "0%"],
        inRange: {
          color: ["#d73027", "#fee08b", "#1a9850"],
        },
      },
      series: [
        {
          type: "heatmap",
          data: seriesData,
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowColor: "rgba(0, 0, 0, 0.25)",
            },
          },
          itemStyle: {
            borderRadius: 4,
            borderColor: "#ffffff",
            borderWidth: 1,
          },
          label: {
            show: false,
          },
        },
      ],
    };

    return (
      <Box sx={{ mt: 3, width: "100%" }}>
        <Box
          id="participant-coverage-heatmap-chart"
          sx={{
            width: "100%",
            minWidth: 1100,
            height: Math.max(560, 220 + metricLabels.length * 46),
            border: "1px solid #e0e0e0",
            borderRadius: 1,
            backgroundColor: "#fff",
            p: 0,
            overflowX: "auto",
          }}
        >
          <ReactECharts
            option={option}
            style={{ width: "100%", height: "100%" }}
            opts={{ renderer: "canvas" }}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mt: 1,
            flexWrap: "wrap",
          }}
        >
          <Typography variant="caption" color="textSecondary">
            Coverage scale:
          </Typography>
          <Box
            sx={{
              width: 220,
              height: 12,
              borderRadius: 999,
              background:
                "linear-gradient(90deg, rgb(215,48,39) 0%, rgb(255,152,80) 50%, rgb(26,150,65) 100%)",
              border: "1px solid #ddd",
            }}
          />
          <Typography variant="caption">0%</Typography>
          <Typography variant="caption">50%</Typography>
          <Typography variant="caption">100%</Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Paper sx={{ p: 3, mt: 3 }}>
      <Stack spacing={3}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Metric Coverage Heatmap
        </Typography>

        {/* Date Range Selection */}
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
            Date Range
          </Typography>

          {/* Preset Buttons */}
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <ToggleButtonGroup
              value={preset}
              exclusive
              onChange={(_, nextPreset) => applyPreset(nextPreset)}
              size="small"
              disabled={running}
              sx={{
                "& .MuiToggleButton-root": {
                  borderColor: "#8C1515",
                  color: "#8C1515",
                  "&.Mui-selected": {
                    backgroundColor: "#8C1515",
                    color: "#FFFFFF",
                    "&:hover": {
                      backgroundColor: "#B33A3A",
                    },
                  },
                },
              }}
            >
              <ToggleButton value="7d">Last 7 Days</ToggleButton>
              <ToggleButton value="1m">Last 1 Month</ToggleButton>
              <ToggleButton value="6m">Last 6 Months</ToggleButton>
              <ToggleButton value="1y">Last 1 Year</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Date Pickers */}
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems="flex-end"
          >
            <TextField
              label="From Date"
              type="date"
              value={fromDate}
              onChange={(e) => {
                setPreset(null);
                setFromDate(e.target.value);
              }}
              InputLabelProps={{ shrink: true }}
              sx={{ width: { xs: "100%", md: 220 } }}
              disabled={running}
            />
            <TextField
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => {
                setPreset(null);
                setEndDate(e.target.value);
              }}
              InputLabelProps={{ shrink: true }}
              sx={{ width: { xs: "100%", md: 220 } }}
              disabled={running}
            />
          </Stack>
        </Box>

        <Divider />

        {/* Aggregation Level Selection */}
        <FormControl fullWidth size="small">
          <InputLabel>Aggregation Level</InputLabel>
          <Select
            value={aggregationType}
            label="Aggregation Level"
            onChange={(e) =>
              setAggregationType(
                e.target.value as "weekly" | "monthly" | "yearly",
              )
            }
          >
            {AGGREGATION_TYPES.map((type) => (
              <MenuItem key={type} value={type}>
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Divider />

        {/* Metric Selection */}
        <Accordion
          expanded={metricsExpanded}
          onChange={(_, expanded) => setMetricsExpanded(expanded)}
          disableGutters
          elevation={0}
          sx={{ border: "1px solid #e0e0e0", borderRadius: 1 }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack spacing={0.25}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Select Metrics
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {selectedMetrics.size} selected of {availableMetrics.length}
              </Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              <Stack
                direction="row"
                spacing={1}
                sx={{ flexWrap: "wrap", gap: 1 }}
              >
                <Button
                  variant="outlined"
                  size="small"
                  onClick={selectAll}
                  disabled={availableMetrics.length === 0}
                >
                  Select All
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={selectCommonOnly}
                  disabled={commonMetricCount === 0}
                >
                  Choose Common Metrics
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={deselectAll}
                  disabled={selectedMetrics.size === 0}
                >
                  Deselect All
                </Button>
              </Stack>

              <FormGroup
                sx={{
                  maxHeight: "220px",
                  overflowY: "auto",
                  border: "1px solid #e0e0e0",
                  p: 1,
                  borderRadius: 1,
                  columnCount: { xs: 1, md: 2, lg: 3 },
                  columnGap: { md: "24px", lg: "32px" },
                }}
              >
                {availableMetrics.map((metric) => (
                  <FormControlLabel
                    key={metric.key}
                    control={
                      <Checkbox
                        checked={selectedMetrics.has(metric.key)}
                        onChange={(e) =>
                          toggleMetric(metric.key, e.target.checked)
                        }
                      />
                    }
                    label={metric.label}
                    sx={{
                      fontSize: "0.9rem",
                      mr: 0,
                      mb: 0.5,
                      breakInside: "avoid",
                      display: "block",
                    }}
                  />
                ))}
              </FormGroup>
            </Stack>
          </AccordionDetails>
        </Accordion>

        {selectedMetrics.size > 0 && (
          <Box sx={{ mt: -1, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {Array.from(selectedMetrics).map((metricKey) => {
              const metric = availableMetrics.find((m) => m.key === metricKey);
              return (
                <Chip
                  key={metricKey}
                  label={metric?.label || metricKey}
                  size="small"
                  onDelete={() => toggleMetric(metricKey, false)}
                />
              );
            })}
          </Box>
        )}

        <Divider />

        {/* Validation Error */}
        {validationError && <Alert severity="error">{validationError}</Alert>}

        {/* Error Display */}
        {error && <Alert severity="error">{error}</Alert>}

        {fetchStatus && <Alert severity="info">{fetchStatus}</Alert>}

        {failedFitbitRanges.length > 0 && (
          <Alert severity="warning">
            Fitbit data could not be retrieved for:{" "}
            {failedFitbitRanges
              .map((range) => `${range.startDate} to ${range.endDate}`)
              .join(", ")}
          </Alert>
        )}

        {/* Generate Button */}
        <Button
          variant="contained"
          onClick={handleGenerateHeatmap}
          disabled={running || selectedMetrics.size === 0}
          startIcon={running ? <CircularProgress size={20} /> : undefined}
        >
          {running ? "Generating..." : "Generate Heatmap"}
        </Button>

        {/* Heatmap Display */}
        {heatmapData && renderHeatmap()}
      </Stack>
    </Paper>
  );
};

export default MetricCoverageHeatmapCard;
